import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";

// Flights service page. Frontend-only; all CTAs route into the existing
// Plan a Trip intake (the single request flow) and Track Trip.

const HANDLE = [
  { t: "Every cabin class", d: "Economy, premium economy, business and first class — matched to your budget and comfort." },
  { t: "Any trip shape", d: "One-way, return and multi-city itineraries, planned end to end." },
  { t: "African regional routes", d: "Short hops and cross-border connections across the continent." },
  { t: "International long-haul", d: "Africa to Europe, the Gulf, Asia and the Americas." },
  { t: "Fare comparison & timing", d: "We compare fares and advise on the best dates and routing." },
  { t: "Changes, baggage & support", d: "Help with date changes, baggage rules and day-of-travel issues." },
];

const ROUTES = [
  "Lagos", "Accra", "Nairobi", "Kigali",
  "Johannesburg", "Cape Town", "Casablanca", "Addis Ababa",
];

const STEPS = [
  { n: "01", t: "Tell us your route", d: "Share origin, destination, dates and travel style." },
  { n: "02", t: "We check suitable fares", d: "We compare options and confirm availability." },
  { n: "03", t: "You confirm and pay securely", d: "You see the final fare before any payment." },
  { n: "04", t: "We support your journey", d: "Before, during and after you fly." },
];

export default function Flights() {
  return (
    <div className="page">
      <Seo
        title="Flights Across Africa and the World — 513Travel"
        description="Request flight options for African regional routes, international trips, multi-city travel, cabin classes and support before and after booking."
        path="/flights"
      />
      {/* ===== Hero ===== */}
      <section className="page-hero flights-hero">
        <div className="container">
          <div className="flights-hero-copy">
            <div className="eyebrow">Flights</div>
            <h1>Flights across Africa and the world</h1>
            <p className="lede">
              From economy to first class, one-way to multi-city, 513Travel helps
              you find the right route, fare and timing — with human support before
              and after booking.
            </p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn gold">Request flights</Link>
              <Link to="/plan-a-trip" className="btn outline">Plan a full trip</Link>
            </div>
          </div>
          <figure className="hero-figure">
            <img
              src="/flights-hero.webp"
              alt="View over an aircraft wing above the clouds at golden hour"
              loading="eager"
              decoding="async"
            />
          </figure>
        </div>
      </section>

      {/* ===== What we handle ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What we handle</div>
            <h2>Flights, handled properly</h2>
            <p>One team to source, book and support your flights — so the details don't fall through the cracks.</p>
          </div>
          <div className="grid grid-3">
            {HANDLE.map((c) => (
              <div className="service-card" key={c.t}>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Africa routes ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Route knowledge</div>
            <h2>Africa routes we understand</h2>
            <p>More than a search box — we know the hubs, the carriers and the connections that make African travel work.</p>
          </div>
          <div className="route-chips">
            {ROUTES.map((r) => (
              <span className="route-chip" key={r}>{r}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>How flight requests work</h2>
          </div>
          <div className="grid grid-4">
            {STEPS.map((s) => (
              <div className="step-card" key={s.n}>
                <span className="step-num" aria-hidden="true">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Trust note ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <p className="demo-note demo-note--center">
            Flight prices can change quickly. We confirm availability and the
            final fare before payment, so you always know what you are paying for.
          </p>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Ready to fly</div>
            <h2>Ready to find your flight?</h2>
            <p>Share your route, dates and travel style. We'll prepare suitable options.</p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn light">Request flights</Link>
              <Link to="/track-trip" className="btn outline-light">Track existing trip</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
